import { useEffect, useState } from 'react'
import { sendGet, sendPost } from 'hooks/use-fetch'
import { Box, Button, CircularProgress } from '@mui/material'
import DataTable from 'Components/Table/DataTable'
import { type PaginationProps, usePagination } from 'hooks/use-pagination'
import { Column, ColumnType } from 'Components/Table/DataTable/DataTable.model'
import { FormatDateTimeValue, SpaceBeforeCapitals } from 'core/Utilities'
import { type CommunicationSummary } from 'Pages/CareNetworkManagement/CareNetworkDetail/CommunicationList'
import { NoteType, type postError, type CareNetworkCommunication } from 'core/types'
import { useAuth } from 'hooks/use-auth'
import { CommunicationDetail } from 'Pages/CareNetworkManagement/CareNetworkDetail/CommunicationDetail'
import { useErrorDispatch } from 'ContextProviders/ErrorProvider'
import { toast } from 'react-toastify'

export interface EnrollmentNotesProps {
  enrollmentID: number
  isNotesPermission: boolean
  isEditable: boolean
  childID: number

}

export function NewEnrollmentNotes (props: EnrollmentNotesProps): JSX.Element {
  const auth = useAuth()
  const dispatch = useErrorDispatch()
  const [loaded, setLoaded] = useState(false)
  const [notes, setNotes] = useState<CommunicationSummary[]>([])
  const [refreshTime, setRefreshTime] = useState(new Date())
  const [selectedCommunication, setSelectedCommunication] = useState<CareNetworkCommunication | null>(null)
  const [openModal, setOpenModal] = useState<boolean>(false)
  const [isRequestLoading, setIsRequestLoading] = useState<boolean>(false)

  useEffect(() => {
    async function getData (): Promise<void> {
      const request = await sendGet(`/NewEnrollment/EnrollmentNotes?enrollmentId=${props.childID}`, {})
      const referralNotes: CommunicationSummary[] = request.response
      setNotes(referralNotes)

      setLoaded(true)
    }
    void getData()
  }, [refreshTime])

  const handleNewClick = (type: NoteType): void => {
    dispatch({ type: 'clear', errors: [] })
    const date = new Date(new Date().toLocaleString('en', { timeZone: 'America/New_York' }))

    const initialCommunicationState: CareNetworkCommunication = {
      userID: auth.user?.id ?? 0,
      addressedWhatMatteredMost: null,
      caregiverID: null,
      contactMethodID: null,
      enteredOn: date,
      id: 0,
      type,
      childID: props.childID,
      needMet: null,
      note: '',
      reasonNotMet: '',
      reminderDate: null,
      referralID: null,
      enrollmentID: props.enrollmentID
    }

    setSelectedCommunication(initialCommunicationState)
    setOpenModal(true)
  }

  const handleSubmit = (com: CareNetworkCommunication): void => {
    const updateCommunication = async (): Promise<void> => {
      if (!isRequestLoading) {
        setIsRequestLoading(true)

        dispatch({ type: 'clear', errors: [] })

        let result: { response: any, error: string[], success: boolean } | null = null
        const isNew = com.id === 0
        const endpoint = `/Communication/${isNew ? 'Create' : 'Update'}Communication`
        result = await sendPost(endpoint, com)
        const rsp: { newRecordID: number, success: boolean, errors: postError[] } = result.response
        if (rsp.success) {
          setOpenModal(false)
          toast.success('Communication record was updated')
          setRefreshTime(new Date())
        } else {
          dispatch({ type: 'update', errors: rsp.errors })
        }
        setIsRequestLoading(false)
      }
    }
    void updateCommunication()
  }

  const typeColumn = new Column('type', 'Type', ColumnType.CUSTOM)
  typeColumn.customCellGenerator = row => {
    return <>{SpaceBeforeCapitals(row.type)}</>
  }
  const noteColumn = new Column('note', 'Message')
  const aboutColumn = new Column('enteredOn', 'Entered On', ColumnType.CUSTOM)
  aboutColumn.customCellGenerator = row => {
    return <div>Added by {row.userName} on {FormatDateTimeValue(row.enteredOn)} </div>
  }

  const columns = [typeColumn, aboutColumn, noteColumn]

  const paginationProps: PaginationProps<CommunicationSummary> = {
    rows: notes,
    initialSize: 5,
    pageSizeSteps: [5, 10]
  }
  const pagination = usePagination(paginationProps)

  if (!loaded) return <CircularProgress role='loading' />

  if (!props.isNotesPermission) {
    return <div>You do not have access to this feature</div>
  }

  return <>
    {props.isEditable && <Box className='d-flex mt-10'>
      <Button variant='contained' color='secondary' onClick={() => { handleNewClick(NoteType.ContactLog) }} data-testid='add-contact-log'>Add Contact Log</Button>
      <Button variant='contained' color='secondary' className='ml-10' onClick={() => { handleNewClick(NoteType.Note) }} data-testid='add-note'>Add Note</Button>
    </Box>
    }

    <DataTable
      name='enrollmentNoteTable'
      page={pagination.page}
      data-testid='enrollmentNoteTable'
      hasCheckbox={false}
      columns={columns}
      rows={pagination.internalRows}
      rowClickable={false}
      sortable={false}
      totalRecords={pagination.recordCount}
      onPageChange={pagination.handleChangePage}
      onRowsPerPageChange={pagination.handleChangeRowsPerPage}
      loading={!loaded}
    />

    {openModal && selectedCommunication !== null && (
      <CommunicationDetail
        onClose={() => { setOpenModal(false) }}
        open={openModal}
        isNew={true}
        selectedCommunication={selectedCommunication}
        onSubmit={handleSubmit}
        userOptions={[]}
      />
    )}
  </>
}
